import React from 'react';

type Variant = 'h1' | 'h2' | 'h3' | 'body' | 'small' | 'label' | 'mono' | 'stat' | 'desc';
type Color = 'primary' | 'secondary' | 'indigo' | 'emerald' | 'rose' | 'amber' | 'slate' | 'white' | 'gray';
type Weight = 'normal' | 'medium' | 'semibold' | 'bold' | 'black';

interface TextProps {
    variant?: Variant;
    color?: Color;
    weight?: Weight;
    align?: 'left' | 'center' | 'right';
    truncate?: boolean;
    className?: string;
    children: React.ReactNode;
    as?: React.ElementType;
    id?: string;
    title?: string;
}

const variants: Record<Variant, string> = {
    h1: 'text-3xl md:text-4xl font-black tracking-tight leading-tight',
    h2: 'text-2xl md:text-3xl font-bold tracking-tight',
    h3: 'text-xl font-bold tracking-tight',
    body: 'text-base leading-relaxed',
    small: 'text-sm font-medium',
    label: 'text-[10px] font-black uppercase tracking-[0.2em]',
    mono: 'font-mono tracking-tighter',
    stat: 'text-4xl font-black tracking-tighter',
    desc: 'text-sm font-medium opacity-70'
};

const colors: Record<Color, string> = {
    primary: 'text-slate-900',
    secondary: 'text-slate-600',
    indigo: 'text-indigo-600',
    emerald: 'text-emerald-600',
    rose: 'text-rose-600',
    amber: 'text-amber-600',
    slate: 'text-slate-400',
    gray: 'text-slate-500',
    white: 'text-white'
};

const weights: Record<Weight, string> = {
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
    bold: 'font-bold',
    black: 'font-black'
};

const Text: React.FC<TextProps> = ({
    variant = 'body',
    color,
    weight,
    align,
    truncate = false,
    className = '',
    children,
    as,
    id,
    title
}) => {
    // Determine the underlying HTML element
    const Component = as || (
        variant === 'h1' ? 'h1' :
        variant === 'h2' ? 'h2' :
        variant === 'h3' ? 'h3' :
        variant === 'label' ? 'label' :
        'p'
    );
    
    const classes = [
        variants[variant],
        color ? colors[color] : '',
        weight ? weights[weight] : '',
        align ? `text-${align}` : '',
        truncate ? 'truncate' : '',
        className
    ].filter(Boolean).join(' ');

    return (
        <Component className={classes} id={id} title={title}>
            {children}
        </Component>
    );
};

export default Text;
